import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Calendar, RefreshCw, Route, Clock } from "lucide-react";
import { Button, Card, CardContent, CardHeader } from "@tabipla/ui";
import { SpotList } from "../SpotList";
import { TimelineView } from "./TimelineView";
import type { ItineraryDayCardProps } from "./types";

/**
 * ItineraryDayCard presenter component.
 * Pure presentation - receives all data and callbacks as props.
 * Does not contain any hooks for data fetching or mutations.
 */
export function ItineraryDayCard({
  itinerary,
  savedToMekkeSpotIds,
  viewMode = "list",
  isRegenerating,
  onAddSpot,
  onEditSpot,
  onDelete,
  onRegenerate,
  onSaveToFavorites,
  onSaveToMekke,
  onTravelModeChange,
  onCalculateTravelTimes,
  isCalculatingTravelTimes,
  onReorder,
}: ItineraryDayCardProps) {
  const { t } = useTranslation();

  const sortedSpots = useMemo(
    () => [...itinerary.spots].sort((a, b) => a.order - b.order),
    [itinerary.spots]
  );

  // Check if travel times need to be calculated
  // Show button if: 2+ spots with coordinates, but some are missing travel info
  const canCalculateTravelTimes = useMemo(() => {
    if (sortedSpots.length < 2) return false;
    const spotsWithCoords = sortedSpots.filter((s) => s.latitude != null && s.longitude != null);
    if (spotsWithCoords.length < 2) return false;
    // Check if any spot (except first) is missing travel time
    return sortedSpots.slice(1).some((s) => s.travelDurationMinutes == null);
  }, [sortedSpots]);

  // Calculate total travel distance and duration for the day
  const travelSummary = useMemo(() => {
    let totalDuration = 0;
    let totalDistance = 0;
    let hasData = false;

    for (const spot of sortedSpots) {
      if (spot.travelDurationMinutes != null) {
        totalDuration += spot.travelDurationMinutes;
        hasData = true;
      }
      if (spot.travelDistanceKm != null) {
        totalDistance += spot.travelDistanceKm;
      }
    }

    return { totalDuration, totalDistance, hasData };
  }, [sortedSpots]);

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* 日数バッジ - 数字を大きく、「日目」を小さく */}
            <div className="flex h-14 w-14 flex-col items-center justify-center rounded-xl bg-primary text-primary-foreground sm:h-16 sm:w-16">
              <span className="text-2xl font-bold leading-none sm:text-3xl">
                {itinerary.dayNumber}
              </span>
              <span className="text-[10px] font-medium sm:text-xs">日目</span>
            </div>
            {/* 日付情報 */}
            <div className="flex items-center gap-1.5 text-base font-medium text-foreground sm:text-lg">
              <Calendar className="h-4 w-4 text-muted-foreground sm:h-5 sm:w-5" />
              {new Date(itinerary.date).toLocaleDateString("ja-JP", {
                weekday: "short",
                month: "long",
                day: "numeric",
              })}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {/* 再生成ボタン */}
            <Button
              variant="ghost"
              size="sm"
              onClick={onRegenerate}
              disabled={isRegenerating}
              className="h-8 gap-1.5"
              title={t("itinerary.regenerateDay")}
            >
              <RefreshCw className={`h-4 w-4 ${isRegenerating ? "animate-spin" : ""}`} />
              <span className="hidden sm:inline">{t("itinerary.regenerateDay")}</span>
            </Button>
          </div>
        </div>
      </CardHeader>

      {/* Travel Summary Bar */}
      {travelSummary.hasData && (
        <div className="flex items-center gap-4 border-t bg-muted/50 px-4 py-2 text-sm text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <Route className="h-4 w-4" />
            <span>
              {t("itinerary.totalDistance", "総距離")}: {travelSummary.totalDistance.toFixed(1)}km
            </span>
          </div>
          <div className="flex items-center gap-1.5">
            <Clock className="h-4 w-4" />
            <span>
              {t("itinerary.totalDuration", "移動時間")}: {travelSummary.totalDuration}
              {t("common.minutes", "分")}
            </span>
          </div>
        </div>
      )}

      <CardContent className="space-y-3">
        {/* スポット追加ボタン - リスト表示時のみ上部に配置 */}
        {viewMode === "list" && (
          <Button
            variant="outline"
            size="sm"
            className="w-full border-dashed"
            onClick={() => onAddSpot()}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            {t("spots.addSpot")}
          </Button>
        )}

        {/* スポット表示 - viewModeで切り替え */}
        {viewMode === "list" ? (
          <SpotList
            spots={sortedSpots}
            onEdit={onEditSpot}
            onDelete={onDelete}
            onSaveToFavorites={onSaveToFavorites}
            onSaveToMekke={onSaveToMekke}
            onTravelModeChange={onTravelModeChange}
            savedToMekkeSpotIds={savedToMekkeSpotIds}
          />
        ) : (
          <TimelineView
            spots={sortedSpots}
            onEdit={onEditSpot}
            onAddSpot={onAddSpot}
            onTravelModeChange={onTravelModeChange}
            onReorder={onReorder}
            tripDate={itinerary.date}
          />
        )}

        {/* 移動時間計算ボタン */}
        {canCalculateTravelTimes && onCalculateTravelTimes && (
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={onCalculateTravelTimes}
            disabled={isCalculatingTravelTimes}
          >
            <Route
              className={`mr-1.5 h-4 w-4 ${isCalculatingTravelTimes ? "animate-pulse" : ""}`}
            />
            {isCalculatingTravelTimes ? "計算中..." : "移動時間を計算"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}
